import { FullSite } from "../../../../dtos/site";
import { ActiveStatus } from "../../../../enums/ActiveStatus";
import { RoomEntryState } from "../../../../enums/roomEntryState";

import { SitesStatsResult } from "./types";

interface Params {
    sites: FullSite[];
    alarmStatus: Map<number, boolean>;
    serviceStatus: Map<number, boolean>;
    roomState: Map<number, { roomEntryState: RoomEntryState }>;
}

export function groupSitesForStats({
    sites,
    alarmStatus,
    serviceStatus,
    roomState,
}: Params): SitesStatsResult {
    const siteIdsOnline = Array.from(serviceStatus.entries())
        .filter(([_, online]) => online)
        .map(([siteId]) => siteId);
    const siteIdsOnlineSet = new Set(siteIdsOnline);

    // Demo sites are excluded from the "Issues Detected" counts below, but still count
    // towards the "Sites Total" stats (sitesOnline, sitesWithRoomInUse, sitesWithRoomIdle).
    const billableSites = sites.filter(site => !site.excludeFromBilling);

    const sitesOffline = billableSites.filter(s =>
        !siteIdsOnlineSet.has(s.id) && s.activeStatus === ActiveStatus.Active
    );
    const sitesOnline = sites.filter(s => siteIdsOnlineSet.has(s.id));

    const sitesWithAlarm = billableSites.filter(site => {
        const dynamicAlarm = alarmStatus.get(site.id);
        return dynamicAlarm !== undefined ? dynamicAlarm : site.activeAlarm;
    });

    const getRoomState = (siteId: number) => roomState.get(siteId);

    const sitesWithRoomStateMatching = (states: RoomEntryState[], baseSites: FullSite[]) =>
        baseSites.filter(site => {
            const dynamicRoomState = getRoomState(site.id);
            const state = dynamicRoomState ? dynamicRoomState.roomEntryState : undefined;
            return state !== undefined && states.includes(state);
        });

    const sitesWithRoomBoxLeftOpen = sitesWithRoomStateMatching([RoomEntryState.BoxLeftOpen], billableSites);
    const sitesWithRoomEntryUnauthorized = sitesWithRoomStateMatching([RoomEntryState.Unauthorized], billableSites);
    const sitesWithRoomInUse = sitesWithRoomStateMatching([
        RoomEntryState.EnteringRoom,
        RoomEntryState.EnteringRoomDoorOpen,
        RoomEntryState.InRoom,
        RoomEntryState.ExitingRoom,
    ], sites);
    const sitesWithRoomIdle = sitesWithRoomStateMatching([RoomEntryState.Idle], sites);

    const issueCount =
        sitesOffline.length +
        sitesWithAlarm.length +
        sitesWithRoomBoxLeftOpen.length +
        sitesWithRoomEntryUnauthorized.length;

    return {
        sitesOffline,
        sitesOnline,
        sitesWithAlarm,
        sitesWithRoomBoxLeftOpen,
        sitesWithRoomEntryUnauthorized,
        sitesWithRoomInUse,
        sitesWithRoomIdle,
        issueCount,
    };
}
